/**
 * The files in a .zip, read in the browser (Sites, 2026-11-03): a website
 * someone zipped goes up file by file, as a dropped folder does, so the server
 * never unpacks anything. Its central directory is read first, then each entry —
 * stored, or deflated and inflated with the browser's own DecompressionStream.
 *
 * Enough for what a site is zipped with — Windows, macOS, 7-Zip, any build tool:
 * no encryption, no ZIP64 (over 4 GB, or 65,535 files), no archive spread over
 * several files. A name is read as UTF-8, which every one of those writes for a
 * name that is not plain English.
 */

export type ZipProblem = 'NOT_ZIP' | 'TOO_BIG' | 'ENCRYPTED' | 'METHOD' | 'BROKEN' | 'UNSUPPORTED';

export class ZipError extends Error {
  constructor(readonly problem: ZipProblem) {
    super(`The .zip cannot be read: ${problem}.`);
    this.name = 'ZipError';
  }
}

export interface ZipEntry {
  /** Its path in the archive, `/` between folders. */
  path: string;
  /** Its size once unpacked. */
  size: number;
  /** Its bytes, unpacked. */
  read: () => Promise<Blob>;
}

const END_OF_DIRECTORY = 0x06054b50;
const DIRECTORY_ENTRY = 0x02014b50;
const LOCAL_HEADER = 0x04034b50;
/** The end-of-directory record: 22 bytes, then a comment of up to 65,535. */
const TAIL_MAX = 22 + 0xffff;

/** Whether this browser can inflate a zip's entries (Chrome 103, Firefox 113, Safari 16.4 and later). */
export function canReadZip(): boolean {
  if (typeof DecompressionStream === 'undefined') return false;
  try {
    new DecompressionStream('deflate-raw' as CompressionFormat);
    return true;
  } catch {
    return false;
  }
}

/** What is in the archive: its files, not its folders. */
export async function readZip(zip: Blob): Promise<ZipEntry[]> {
  if (!canReadZip()) throw new ZipError('UNSUPPORTED');
  const tailSize = Math.min(zip.size, TAIL_MAX);
  if (tailSize < 22) throw new ZipError('NOT_ZIP');
  const tail = new DataView(await zip.slice(zip.size - tailSize).arrayBuffer());
  let end = -1;
  for (let at = tailSize - 22; at >= 0; at--) {
    if (tail.getUint32(at, true) === END_OF_DIRECTORY) {
      end = at;
      break;
    }
  }
  if (end < 0) throw new ZipError('NOT_ZIP');

  const count = tail.getUint16(end + 10, true);
  const directorySize = tail.getUint32(end + 12, true);
  const directoryAt = tail.getUint32(end + 16, true);
  if (count === 0xffff || directorySize === 0xffffffff || directoryAt === 0xffffffff) throw new ZipError('TOO_BIG');
  if (directoryAt + directorySize > zip.size) throw new ZipError('BROKEN');

  const directory = new DataView(await zip.slice(directoryAt, directoryAt + directorySize).arrayBuffer());
  const names = new TextDecoder('utf-8');
  const entries: ZipEntry[] = [];
  let at = 0;
  for (let n = 0; n < count; n++) {
    if (at + 46 > directory.byteLength || directory.getUint32(at, true) !== DIRECTORY_ENTRY) throw new ZipError('BROKEN');
    const flags = directory.getUint16(at + 8, true);
    const method = directory.getUint16(at + 10, true);
    const packed = directory.getUint32(at + 20, true);
    const size = directory.getUint32(at + 24, true);
    const nameLength = directory.getUint16(at + 28, true);
    const extraLength = directory.getUint16(at + 30, true);
    const commentLength = directory.getUint16(at + 32, true);
    const headerAt = directory.getUint32(at + 42, true);
    const path = names.decode(new Uint8Array(directory.buffer, directory.byteOffset + at + 46, nameLength));
    at += 46 + nameLength + extraLength + commentLength;

    // A folder of its own: the files in it say where they go.
    if (path.endsWith('/') || path.endsWith('\\')) continue;
    if (packed === 0xffffffff || size === 0xffffffff || headerAt === 0xffffffff) throw new ZipError('TOO_BIG');
    if (flags & 0x1) throw new ZipError('ENCRYPTED');
    if (method !== 0 && method !== 8) throw new ZipError('METHOD');

    entries.push({ path: path.replace(/\\/g, '/'), size, read: () => readEntry(zip, headerAt, packed, size, method) });
  }
  return entries;
}

/** One entry's bytes: after its own header, stored as they are, or deflated. */
async function readEntry(zip: Blob, headerAt: number, packed: number, size: number, method: number): Promise<Blob> {
  const header = new DataView(await zip.slice(headerAt, headerAt + 30).arrayBuffer());
  if (header.byteLength < 30 || header.getUint32(0, true) !== LOCAL_HEADER) throw new ZipError('BROKEN');
  const dataAt = headerAt + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  const data = zip.slice(dataAt, dataAt + packed);
  const bytes =
    method === 0
      ? data
      : await new Response(data.stream().pipeThrough(new DecompressionStream('deflate-raw' as CompressionFormat))).blob();
  if (bytes.size !== size) throw new ZipError('BROKEN');
  return bytes;
}
